// UltraFlex Admin Layout — Grouped Navigation with Collapsible Sections
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../contexts/SiteContext';
import SiteSelector from '../../components/SiteSelector';
import {
  LayoutDashboard, Users, CreditCard, DoorOpen, ShoppingBag,
  BarChart3, Building2, UserCog, Bell, Settings, LogOut,
  Menu, X, ChevronDown, Activity, Package, Dumbbell, FileSpreadsheet,
  Target, Mail, Database, Cpu, ShieldAlert, Shield, PoundSterling,
  ChevronRight, Briefcase, ScanLine, CalendarDays, ClipboardList,
  UserCheck, FileBarChart, Layers, Eye, Warehouse, Clock,
  TrendingUp, PackageCheck, Coins, Globe, UserPlus, Book, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Grouped navigation structure with required permissions
const NAV_GROUPS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    items: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    ],
  },
  {
    id: 'members',
    label: 'Members',
    icon: Users,
    requires: ['members.view'],
    items: [
      { path: '/admin/members', icon: Users, label: 'All Members', perm: 'members.view' },
      { path: '/admin/subscriptions', icon: CreditCard, label: 'Subscriptions', perm: 'billing.view' },
      { path: '/admin/billing', icon: PoundSterling, label: 'Billing', perm: 'billing.view' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Building2,
    requires: ['access.view', 'banned.view'],
    items: [
      { path: '/admin/access', icon: DoorOpen, label: 'Access Control', perm: 'access.view' },
      { path: '/admin/devices', icon: Cpu, label: 'Scanner Devices', perm: 'access.manage_devices' },
      { path: '/admin/day-passes', icon: CalendarDays, label: 'Day Passes', perm: 'members.view' },
      { path: '/admin/assets', icon: Building2, label: 'Asset Management', perm: 'access.view' },
      { path: '/admin/banned', icon: ShieldAlert, label: 'Banned List', perm: 'banned.view' },
      { path: '/admin/applications', icon: ClipboardList, label: 'Review Queue', perm: 'banned.review' },
      { path: '/admin/pt-detection', icon: Eye, label: 'PT Detection', perm: 'access.view' },
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    icon: Briefcase,
    requires: ['leadgen.view', 'shop.view'],
    items: [
      { path: '/admin/crm', icon: Target, label: 'Lead Gen', perm: 'leadgen.view' },
      { path: '/admin/tours', icon: CalendarDays, label: 'Tours', perm: 'leadgen.view' },
      { path: '/admin/shop', icon: ShoppingBag, label: 'Shop & Orders', perm: 'shop.view' },
      { path: '/admin/epos-backoffice', icon: ScanLine, label: 'EPOS Back Office', perm: 'shop.view' },
    ],
  },
  {
    id: 'stock',
    label: 'Stock',
    icon: Warehouse,
    requires: ['shop.manage'],
    items: [
      { path: '/admin/products', icon: Package, label: 'Products & Pricing', perm: 'shop.manage' },
      { path: '/admin/stock', icon: PackageCheck, label: 'Stock Management', perm: 'shop.manage' },
      { path: '/admin/suppliers', icon: Briefcase, label: 'Suppliers', perm: 'shop.manage' },
      { path: '/admin/purchase-orders', icon: ClipboardList, label: 'Purchase Orders', perm: 'shop.manage' },
      { path: '/admin/inventory-reports', icon: FileBarChart, label: 'Inventory Reports', perm: 'reports.view' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: PoundSterling,
    requires: ['finance.view'],
    items: [
      { path: '/admin/finance-dashboard', icon: PoundSterling, label: 'Finance Dashboard', perm: 'finance.view' },
      { path: '/admin/dd-files', icon: CreditCard, label: 'DD Collections', perm: 'finance.view' },
      { path: '/admin/dd-lifecycle', icon: AlertTriangle, label: 'DD Lifecycle', perm: 'finance.view' },
      { path: '/admin/revenue', icon: TrendingUp, label: 'Revenue Recognition', perm: 'finance.view' },
      { path: '/admin/group-overview', icon: Globe, label: 'Group Overview', perm: 'finance.view' },
      { path: '/admin/ledgers', icon: Database, label: 'Ledgers Explorer', perm: 'reports.view' },
    ],
  },
  {
    id: 'staff',
    label: 'Staff',
    icon: UserCog,
    requires: ['staff.view'],
    items: [
      { path: '/admin/staff', icon: UserCheck, label: 'Staff Management', perm: 'staff.view' },
      { path: '/staff-portal', icon: Dumbbell, label: 'Staff Portal' },
      { path: '/admin/personal-trainers', icon: Dumbbell, label: 'Personal Trainers', perm: 'staff.view' },
      { path: '/admin/payroll', icon: Coins, label: 'Payroll', perm: 'staff.view' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    requires: ['reports.view'],
    items: [
      { path: '/admin/reports', icon: FileBarChart, label: 'Reports', perm: 'reports.view' },
      { path: '/admin/report-builder', icon: Database, label: 'Report Builder', perm: 'reports.view' },
      { path: '/admin/audit-logs', icon: Shield, label: 'Audit Logs', perm: 'reports.view' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    requires: ['settings.plans', 'settings.templates', 'settings.sites', 'settings.roles', 'members.export', 'staff.create'],
    items: [
      { path: '/admin/users', icon: UserPlus, label: 'User Management', perm: 'staff.create' },
      { path: '/admin/plans', icon: CreditCard, label: 'Plans', perm: 'settings.plans' },
      { path: '/admin/email-templates', icon: Bell, label: 'Email Templates', perm: 'settings.templates' },
      { path: '/admin/sites', icon: Building2, label: 'Sites', perm: 'settings.sites' },
      { path: '/admin/permissions', icon: Shield, label: 'Roles & Permissions', perm: 'settings.roles' },
      { path: '/admin/epos-configs', icon: Cpu, label: 'EPOS Till Configs', perm: 'settings.sites' },
      { path: '/admin/sar-export', icon: FileSpreadsheet, label: 'GDPR / SAR Export', perm: 'members.export' },
    ],
  },
  {
    id: 'help',
    label: 'Help & Training',
    icon: Book,
    items: [
      { path: '/admin/knowledge-base', icon: Book, label: 'Knowledge Base' },
    ],
  },
];

// Filter nav groups based on user's permissions
const filterNavGroups = (groups, hasAnyPermission, hasPermission) => {
  return groups
    .map(group => {
      // Groups without requires are always visible (e.g. overview)
      if (!group.requires) return group;
      // If user doesn't have ANY of the required permissions, hide the group entirely
      if (!hasAnyPermission(group.requires)) return null;
      // Filter items within the group (hide items the user can't access)
      const visibleItems = group.items.filter(item => !item.perm || hasPermission(item.perm));
      if (visibleItems.length === 0) return null;
      return { ...group, items: visibleItems };
    })
    .filter(Boolean);
};

const NavGroup = ({ group, isOpen, onToggle, currentPath, sidebarOpen, onNavigate }) => {
  const isActive = group.items.some(item =>
    item.exact ? currentPath === item.path : currentPath.startsWith(item.path)
  );
  const Icon = group.icon;

  // Single-item groups don't need collapsing
  if (group.items.length === 1) {
    const item = group.items[0];
    const ItemIcon = item.icon;
    const active = item.exact ? currentPath === item.path : currentPath.startsWith(item.path);
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
          active
            ? 'bg-uf-red/15 text-uf-red font-semibold'
            : 'text-uf-text-muted hover:text-uf-text hover:bg-uf-surface-hi'
        }`}
      >
        <ItemIcon className="w-4 h-4 flex-shrink-0" />
        {sidebarOpen && <span>{item.label}</span>}
      </Link>
    );
  }

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        data-testid={`nav-group-${group.id}`}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
          isActive ? 'text-uf-text font-semibold' : 'text-uf-text-muted hover:text-uf-text'
        }`}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-uf-red' : ''}`} />
        {sidebarOpen && (
          <>
            <span className="flex-1 text-left">{group.label}</span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          </>
        )}
      </button>
      {isOpen && sidebarOpen && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-uf-border/40 pl-3">
          {group.items.map(item => {
            const ItemIcon = item.icon;
            const active = item.exact ? currentPath === item.path : currentPath.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-xs transition-all ${
                  active
                    ? 'bg-uf-red/10 text-uf-red font-medium'
                    : 'text-uf-text-muted hover:text-uf-text hover:bg-uf-surface-hi'
                }`}
              >
                <ItemIcon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { hasPermission, hasAnyPermission, loading: siteLoading } = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filter nav based on user permissions (via SiteContext — unified source of truth)
  const visibleGroups = filterNavGroups(NAV_GROUPS, hasAnyPermission, hasPermission);

  // Track which groups are open
  const [openGroups, setOpenGroups] = useState(() => {
    const activeGroup = visibleGroups.find(g =>
      g.items.some(item => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path))
    );
    return activeGroup ? { [activeGroup.id]: true } : { overview: true };
  });

  // Auto-open the group containing the current route
  useEffect(() => {
    const activeGroup = visibleGroups.find(g =>
      g.items.some(item => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path))
    );
    if (activeGroup && !openGroups[activeGroup.id]) {
      setOpenGroups(prev => ({ ...prev, [activeGroup.id]: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex h-screen bg-uf-bg overflow-hidden" data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-uf-surface border-r border-uf-border transition-all duration-200 ${
          sidebarOpen ? 'w-60' : 'w-16'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-uf-border flex items-center gap-3">
          {sidebarOpen ? (
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-uf-red rounded-lg flex items-center justify-center text-white font-bold text-sm">UF</div>
              <span className="text-uf-text font-bold text-sm">UltraFlex Admin</span>
            </Link>
          ) : (
            <div className="w-8 h-8 bg-uf-red rounded-lg flex items-center justify-center text-white font-bold text-sm mx-auto">UF</div>
          )}
        </div>

        {/* Nav Groups */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" data-testid="admin-nav">
          {visibleGroups.map(group => (
            <NavGroup
              key={group.id}
              group={group}
              isOpen={openGroups[group.id]}
              onToggle={() => toggleGroup(group.id)}
              currentPath={location.pathname}
              sidebarOpen={sidebarOpen}
              onNavigate={() => {}}
            />
          ))}
        </nav>

        {/* User & Collapse */}
        <div className="p-3 border-t border-uf-border">
          {sidebarOpen && (
            <div className="flex items-center gap-2 mb-3 px-2">
              <div className="w-7 h-7 bg-uf-red/20 rounded-full flex items-center justify-center text-uf-red text-xs font-bold flex-shrink-0">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-uf-text text-xs font-medium truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-uf-text-muted text-[10px] capitalize truncate">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          )}
          <div className={`flex items-center ${sidebarOpen ? 'gap-1' : 'flex-col gap-2'}`}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 text-uf-text-muted hover:text-uf-text rounded-lg hover:bg-uf-surface-hi text-center ${sidebarOpen ? 'flex-1' : 'w-full'}`}
              data-testid="toggle-sidebar"
            >
              <Menu className="w-4 h-4 mx-auto" />
            </button>
            <button
              onClick={handleLogout}
              className={`p-2 text-uf-text-muted hover:text-red-400 rounded-lg hover:bg-red-500/10 ${sidebarOpen ? '' : 'w-full'}`}
              data-testid="admin-logout-btn"
              title="Sign out"
            >
              <LogOut className="w-4 h-4 mx-auto" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-uf-surface border-b border-uf-border px-4 py-3 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-uf-red rounded-lg flex items-center justify-center text-white font-bold text-xs">UF</div>
          <span className="text-uf-text font-bold text-sm">Admin</span>
        </Link>
        <div className="flex items-center gap-2">
          <SiteSelector compact variant="mobile" />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-uf-text" data-testid="admin-mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute left-0 top-14 bottom-0 w-72 bg-uf-surface border-r border-uf-border overflow-y-auto" onClick={e => e.stopPropagation()}>
            <nav className="p-3 space-y-0.5">
              {visibleGroups.map(group => (
                <NavGroup
                  key={group.id}
                  group={group}
                  isOpen={openGroups[group.id]}
                  onToggle={() => toggleGroup(group.id)}
                  currentPath={location.pathname}
                  sidebarOpen={true}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              ))}
            </nav>
            <div className="p-3 border-t border-uf-border">
              <div className="flex items-center gap-2 mb-3 px-2">
                <div className="w-7 h-7 bg-uf-red/20 rounded-full flex items-center justify-center text-uf-red text-xs font-bold flex-shrink-0">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-uf-text text-xs font-medium truncate">{user?.first_name} {user?.last_name}</p>
                  <p className="text-uf-text-muted text-[10px] capitalize truncate">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
              <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-sm">
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:pt-0 pt-14">
        {/* Desktop Top Bar — Site Selector + User Badge */}
        <header className="hidden lg:flex items-center justify-between bg-uf-surface border-b border-uf-border px-6 py-2.5" data-testid="admin-topbar">
          <div className="flex items-center gap-3">
            <SiteSelector variant="desktop" />
            {siteLoading && <span className="text-[11px] text-uf-text-muted">Loading permissions…</span>}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-2 py-1 rounded-lg">
              <div className="w-6 h-6 bg-uf-red/20 rounded-full flex items-center justify-center text-uf-red text-[10px] font-bold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="text-xs">
                <div className="text-uf-text font-medium leading-tight">{user?.first_name} {user?.last_name}</div>
                <div className="text-uf-text-muted text-[10px] capitalize leading-tight">{user?.role?.replace('_', ' ')}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto" data-testid="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
